import React, { useState, useEffect } from 'react';
import { X, User, IndianRupee, Save, Loader2, MessageSquare, Landmark, Calendar, Info } from 'lucide-react';
import styles from './Loans.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const LoanDrawer = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    loan_amount: '',
    emi_amount: '',
    total_emis: '',
    purpose: '',
    remarks: ''
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);

  const fetchEmployees = async () => {
    setFetchingEmployees(true);
    try {
      const response = await api.get('/employees-dropdown');
      if (response.data.success) {
        setEmployees(response.data.data.map(e => ({
          value: e.id,
          label: `${e.employee_name} (${e.designation_name || e.employee_code})`
        })));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setFetchingEmployees(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const isAdmin = storedUser.role?.toLowerCase() === 'admin';
      const defaultEmployeeId = !isAdmin ? (storedUser.employee_id || storedUser.id)?.toString() : '';

      setFormData({
        employee_id: defaultEmployeeId,
        loan_amount: '',
        emi_amount: '',
        total_emis: '',
        purpose: '',
        remarks: ''
      });
    }
  }, [isOpen]);

  // Auto-calculate Total EMIs if Loan Amount and EMI Amount are populated
  useEffect(() => {
    const loanAmt = parseFloat(formData.loan_amount);
    const emiAmt = parseFloat(formData.emi_amount);
    if (!isNaN(loanAmt) && !isNaN(emiAmt) && emiAmt > 0) {
      const calculatedEmis = Math.ceil(loanAmt / emiAmt);
      setFormData(prev => ({ ...prev, total_emis: calculatedEmis.toString() }));
    }
  }, [formData.loan_amount, formData.emi_amount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.loan_amount || !formData.emi_amount || !formData.total_emis) {
      toast.error('Please fill in all required fields');
      return;
    }

    const loanAmt = parseFloat(formData.loan_amount);
    const emiAmt = parseFloat(formData.emi_amount);
    const totalEmis = parseInt(formData.total_emis);

    if (loanAmt < 1000 || loanAmt > 1000000) {
      toast.error('Loan amount must be between ₹1,000 and ₹10,00,000');
      return;
    }

    if (emiAmt < 500) {
      toast.error('EMI amount must be at least ₹500');
      return;
    }



    setLoading(true);
    try {
      await api.post('/loans', {
        employee_id: parseInt(formData.employee_id),
        loan_amount: loanAmt,
        emi_amount: emiAmt,
        total_emis: totalEmis,
        purpose: formData.purpose,
        remarks: formData.remarks
      });
      toast.success('Loan request submitted successfully');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving loan request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit loan request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
              Request New Loan
            </h2>
            <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>Draft employee long-term loan records.</p>
          </div>
          <button
            className={styles['close-icon-btn']}
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {/* Employee dropdown */}
            <div className={styles['form-group']}>
              <label><User size={16} /> Employee *</label>
              {(() => {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const isAdmin = storedUser.role?.toLowerCase() === 'admin';

                if (isAdmin) {
                  return (
                    <CustomSelect
                      options={employees}
                      value={employees.find(e => e.value === formData.employee_id)}
                      onChange={(opt) => setFormData({ ...formData, employee_id: opt.value })}
                      placeholder="Select Employee"
                      isLoading={fetchingEmployees}
                    />
                  );
                } else {
                  return (
                    <div className={styles['form-input']} style={{ backgroundColor: '#f1f5f9', color: '#475569', cursor: 'not-allowed', display: 'flex', alignItems: 'center', height: '40px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
                      {storedUser.name || 'Current User'}
                    </div>
                  );
                }
              })()}
            </div>

            {/* Loan Amount */}
            <div className={styles['form-group']}>
              <label><IndianRupee size={16} /> Loan Amount *</label>
              <input
                type="number"
                className={styles['form-input']}
                placeholder="e.g. ₹50,000"
                value={formData.loan_amount}
                onChange={(e) => setFormData({ ...formData, loan_amount: e.target.value })}
              />
            </div>

            {/* EMI Amount */}
            <div className={styles['form-group']}>
              <label><IndianRupee size={16} /> EMI Amount (Monthly) *</label>
              <input
                type="number"
                className={styles['form-input']}
                placeholder="e.g. ₹5,000"
                value={formData.emi_amount}
                onChange={(e) => setFormData({ ...formData, emi_amount: e.target.value })}
              />
            </div>

            {/* Total EMIs */}
            <div className={styles['form-group']}>
              <label><Calendar size={16} /> Total EMIs (Months) *</label>
              <input
                type="number"
                className={styles['form-input']}
                placeholder="Auto-calculated or custom"
                value={formData.total_emis}
                onChange={(e) => setFormData({ ...formData, total_emis: e.target.value })}
              />
              <span style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                <Info size={12} /> Auto-calculated from Loan Amount & EMI Amount.
              </span>
            </div>

            {/* Purpose */}
            <div className={styles['form-group']}>
              <label><Landmark size={16} /> Purpose of Loan</label>
              <input
                type="text"
                className={styles['form-input']}
                placeholder="e.g. Medical emergency, home renovation"
                value={formData.purpose}
                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              />
            </div>

            {/* Remarks */}
            <div className={styles['form-group']}>
              <label><MessageSquare size={16} /> Remarks</label>
              <textarea
                className={styles['form-input']}
                style={{ minHeight: '80px', resize: 'vertical' }}
                placeholder="Any special remarks or justifications..."
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
              <span>Submit Request</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanDrawer;
